"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { isExamInfoPage } from "@/lib/route-layout";
import { ADSENSE_SDK_URL, ADSENSE_USE_MOCK } from "@/lib/adsense";
import { installAdSenseMock } from "@/lib/adsense-mock";

/**
 * AdSense SDK(adsbygoogle.js)를 hydration 이후에 주입하는 컴포넌트 (JCG-2053).
 *
 * 배경: layout.tsx에서 <script async>로 직접 렌더하면 React 19가 이를 head로
 * hoist하여 즉시 async 로드한다. 그러면 adsbygoogle.js가 hydration과 race하며
 * DOM을 변형(Auto Ads 노드 주입 등)해 서버 HTML과 클라이언트가 불일치 →
 * React error #418(hydration mismatch)이 전 페이지에서 발생했다.
 *
 * useEffect는 SSR에서 실행되지 않고 클라이언트에서도 commit(=hydration 완료)
 * 이후에만 실행되므로, 여기서 스크립트를 주입하면 hydration reconcile이 끝난
 * 뒤에야 SDK가 로드되어 race가 구조적으로 불가능하다. next/script가 아니므로
 * data-nscript 속성도 붙지 않아 AdSense 콘솔 경고도 없다.
 */
export default function AdSenseScript() {
  const pathname = usePathname();
  const adsDisabled = isExamInfoPage(pathname);

  useEffect(() => {
    // 직접 진입 시 SDK도 요청하지 않는다. 이전 페이지에서 로드된 Auto Ads는
    // AdSense 계정의 페이지 제외 설정으로 별도 차단해야 한다.
    if (adsDisabled) return;

    // dev/목업 모드: 실제 adsbygoogle.js 대신 mock SDK 설치
    if (ADSENSE_USE_MOCK) {
      installAdSenseMock();
      return;
    }

    // StrictMode 중복 실행 / 재마운트 시 중복 삽입 방지
    if (
      document.querySelector(
        'script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
      )
    ) {
      return;
    }

    const script = document.createElement("script");
    script.src = ADSENSE_SDK_URL;
    script.async = true;
    script.crossOrigin = "anonymous";
    document.head.appendChild(script);
  }, [adsDisabled]);

  return null;
}
